import React from 'react';
import { TakeTourButton } from '../../tour';

/**
 * FilesExplorerHeader Component
 * Breadcrumbs, folder title, search trigger button, Active/Archived switcher, Grid/List toggle, and Tour Guide
 */
export default function FilesExplorerHeader({
    projectName = 'Shopify Store Redesign',
    folderName = 'Shared Documents',
    fileCount = null,
    viewMode = 'grid',
    onViewModeChange,
    onOpenSearch,
    onUploadClick,
    onRefresh,
    isRefreshing = false,
    fileStatus = 'active',
    onFileStatusChange,
    isArchivedProject = false,
    onNavigate = null,
    hideSearch = false,
}) {
    return (
        <div data-tour="files-explorer-header" className="flex flex-col gap-3 mb-6">
            {/* Top row: breadcrumb + title on left, actions on right — wraps on small screens */}
            <div className="flex flex-wrap justify-between items-start gap-3">
                {/* Left: Breadcrumb + Title */}
                <div className="min-w-0">
                    {/* Breadcrumbs */}
                    <nav className="flex items-center flex-wrap text-xs font-medium text-[#727786] dark:text-[#9aa5bc] mb-1 gap-0.5">
                        <span className="hover:text-[#0070F3] cursor-pointer transition-colors truncate max-w-[160px]" title={projectName}>
                            {projectName}
                        </span>
                        <span className="material-symbols-outlined text-sm">
                            chevron_right
                        </span>
                        <span className="text-[#0b1c30] dark:text-[#eaf1ff] font-semibold truncate max-w-[160px]" title={folderName}>
                            {folderName}
                        </span>
                        {isArchivedProject && (
                            <span className="ml-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20 whitespace-nowrap">
                                Archived Project
                            </span>
                        )}
                    </nav>

                    {/* Title + file count badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                        <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[#0b1c30] dark:text-[#eaf1ff] break-words">
                            {folderName}
                        </h2>
                        {typeof fileCount === 'number' && (
                            <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full border shadow-2xs whitespace-nowrap ${fileStatus === 'archived' || isArchivedProject
                                    ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
                                    : 'bg-[#0070F3]/10 text-[#0070F3] dark:bg-[#0070F3]/25 dark:text-[#60a5fa] border-[#0070F3]/20'
                                }`}>
                                {fileCount} {fileCount === 1 ? 'file' : 'files'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Right: Actions — wraps gracefully on narrow screens */}
                <div className="flex items-center gap-2 flex-wrap">
                    {/* Upload Button */}
                    {!isArchivedProject && (
                        <button
                            data-tour="files-upload-btn"
                            type="button"
                            onClick={onUploadClick}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#0070F3] hover:bg-[#0059c5] text-white text-xs font-semibold shadow-sm transition-all active:scale-95 cursor-pointer whitespace-nowrap"
                        >
                            <span className="material-symbols-outlined text-base leading-none">upload_file</span>
                            <span>Upload</span>
                        </button>
                    )}

                    {/* Active vs Archived Files Switcher */}
                    {!isArchivedProject && (
                        <div data-tour="files-status-filter" className="flex bg-[#eff4ff] dark:bg-[#1a2538] p-1 rounded-lg border border-[#c1c6d7] dark:border-[#344258]">
                            <button
                                type="button"
                                onClick={() => onFileStatusChange?.('active')}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${fileStatus === 'active'
                                        ? 'bg-[#0070F3] text-white shadow-xs'
                                        : 'text-[#555d73] dark:text-[#9aa5bc] hover:text-[#0b1c30] dark:hover:text-white'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">folder</span>
                                <span>Active</span>
                            </button>
                            <button
                                type="button"
                                onClick={() => onFileStatusChange?.('archived')}
                                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${fileStatus === 'archived'
                                        ? 'bg-[#ba1a1a] text-white shadow-xs'
                                        : 'text-[#555d73] dark:text-[#9aa5bc] hover:text-[#ba1a1a] dark:hover:text-[#ffdad6]'
                                    }`}
                            >
                                <span className="material-symbols-outlined text-sm">archive</span>
                                <span>Archived</span>
                            </button>
                        </div>
                    )}

                    {/* Search button (icon-only) */}
                    <button
                        data-tour="files-search-btn"
                        type="button"
                        onClick={onOpenSearch}
                        className={`${hideSearch ? 'hidden ' : ''}p-2 text-[#414754] dark:text-[#9aa5bc] hover:text-[#0070F3] dark:hover:text-white hover:bg-[#eff4ff] dark:hover:bg-[#212f45] rounded-lg transition-colors border border-[#c1c6d7]/60 dark:border-[#344258] shadow-2xs cursor-pointer`}
                        title="Search files (Cmd/Ctrl + K)"
                        aria-label="Search files"
                    >
                        <span className="material-symbols-outlined text-lg leading-none">search</span>
                    </button>

                    {/* Grid / List switcher */}
                    <div data-tour="files-viewmode-toggle" className="flex bg-[#eff4ff] dark:bg-[#1a2538] p-1 rounded-lg border border-[#c1c6d7] dark:border-[#344258]">
                        <button
                            type="button"
                            onClick={() => onViewModeChange?.('grid')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'grid'
                                    ? 'bg-[#0070F3] text-white shadow-sm'
                                    : 'text-[#414754] dark:text-[#9aa5bc] hover:bg-white/50 dark:hover:bg-[#212f45]'
                                }`}
                        >
                            <span className="material-symbols-outlined text-base">grid_view</span>
                            <span>Grid</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => onViewModeChange?.('list')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${viewMode === 'list'
                                    ? 'bg-[#0070F3] text-white shadow-sm'
                                    : 'text-[#414754] dark:text-[#9aa5bc] hover:bg-white/50 dark:hover:bg-[#212f45]'
                                }`}
                        >
                            <span className="material-symbols-outlined text-base">list</span>
                            <span>List</span>
                        </button>
                    </div>

                    {/* Tour Guide Button */}
                    <TakeTourButton currentNav="files" onNavigate={onNavigate} />

                    {/* Refresh button */}
                    <button
                        type="button"
                        onClick={onRefresh}
                        disabled={isRefreshing}
                        className="p-1.5 text-[#727786]/50 dark:text-[#9aa5bc]/40 hover:text-[#414754] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-all opacity-60 hover:opacity-100 cursor-pointer"
                        title="Refresh files"
                        aria-label="Refresh files"
                    >
                        <span className={`material-symbols-outlined text-base leading-none ${isRefreshing ? 'animate-spin text-[#0070F3]' : ''}`}>
                            refresh
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}
